import { useEffect } from "react";
import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/use-auth";
import { Toaster } from "@/components/ui/sonner";
import { CookieBanner } from "@/components/CookieBanner";
import { Sentry } from "@/lib/sentry";
import i18n, { applyClientLocale } from "@/lib/i18n";

/**
 * Stale dynamic-import recovery.
 *
 * After a Vite HMR restart or new deploy, previously-loaded chunks reference
 * hashed filenames that no longer exist on the server. The next click that
 * triggers a code-split import then fails with "Failed to fetch dynamically
 * imported module" and the UI silently does nothing (e.g. Subscribe buttons).
 *
 * We listen once globally and force a one-shot reload to pick up the fresh
 * chunk graph. The sessionStorage guard prevents an infinite reload loop if
 * the underlying problem isn't a stale module.
 */
function useStaleChunkRecovery() {
  useEffect(() => {
    const RELOAD_KEY = "lov:stale-chunk-reload";
    const isStaleChunkError = (msg?: string) =>
      !!msg &&
      (msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module"));

    const handle = (msg?: string) => {
      if (!isStaleChunkError(msg)) return;
      if (sessionStorage.getItem(RELOAD_KEY)) return; // already tried — give up
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
    };

    const onError = (e: ErrorEvent) => handle(e?.message);
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason: any = e?.reason;
      handle(typeof reason === "string" ? reason : reason?.message);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Clear the guard on a successful navigation/load so future stale
    // chunks (e.g. after the next deploy) can also auto-recover.
    const clearTimer = setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 5_000);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      clearTimeout(clearTimer);
    };
  }, []);
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold font-display text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Tack. Förvandlar kunder till stammisar" },
      { name: "description", content: "Få kunder att komma tillbaka med QR-baserad retention. Byggt för caféer, salonger och butiker. Prova gratis i 30 dagar." },
      { property: "og:title", content: "Tack. Förvandlar kunder till stammisar" },
      { property: "og:description", content: "Få kunder att komma tillbaka med QR-baserad retention. Byggt för caféer, salonger och butiker. Prova gratis i 30 dagar." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company" },
      { property: "og:image", content: "https://tack.company/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Tack QR posters at a hair salon, nail studio and restaurant counter — turn customers into regulars" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tack. Förvandlar kunder till stammisar" },
      { name: "twitter:description", content: "Få kunder att komma tillbaka med QR-baserad retention. Byggt för caféer, salonger och butiker. Prova gratis i 30 dagar." },
      { name: "twitter:image", content: "https://tack.company/og-image.png" },
      { name: "twitter:image:alt", content: "Tack QR posters at a hair salon, nail studio and restaurant counter — turn customers into regulars" },
      { name: "theme-color", content: "#D47B5E" },
      { name: "google-site-verification", content: "xvrnTaV8TyeCz-MUS6w6P9kqeklTlx9YloTt0_NbxVQ" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // DM Sans — loaded with display=swap so first paint isn't blocked.
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      // Plausible Analytics — privacy-first, cookieless, GDPR-clean.
      // Site: tack.company
      {
        src: "https://plausible.io/js/pa-beDagvRNxW1CpyROawO8k.js",
        async: true,
        defer: true,
      } as React.ScriptHTMLAttributes<HTMLScriptElement>,
      {
        children:
          "window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};plausible.init()",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://tack.company/#organization",
              name: "Tack",
              url: "https://tack.company",
              logo: "https://tack.company/tack-logo.png",
              description:
                "QR-based customer retention for small businesses. Capture feedback, reward loyalty, and grow repeat visits.",
              email: "hello@tack.company",
              foundingLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressCountry: "SE",
                },
              },
              sameAs: [],
            },
            {
              "@type": "WebSite",
              "@id": "https://tack.company/#website",
              url: "https://tack.company",
              name: "Tack",
              description:
                "Turn satisfied customers into returning ones with QR-based retention.",
              publisher: { "@id": "https://tack.company/#organization" },
              inLanguage: "en",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useStaleChunkRecovery();
  useEffect(() => {
    applyClientLocale();
  }, []);
  return (
    <I18nextProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div className="flex min-h-screen items-center justify-center bg-background px-4">
            <div className="max-w-md text-center">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Something went wrong
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                An unexpected error occurred. We've been notified.
              </p>
              {import.meta.env.DEV && (error as Error)?.message && (
                <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left font-mono text-xs text-destructive">
                  {(error as Error).message}
                </pre>
              )}
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  onClick={resetError}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Try again
                </button>
                <a
                  href="/"
                  className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  Go home
                </a>
              </div>
            </div>
          </div>
        )}
      >
        <AuthProvider>
          <Outlet />
          <Toaster />
          <CookieBanner />
        </AuthProvider>
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
    </I18nextProvider>
  );
}
